import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import api, { apiError } from "@/lib/api";
import { isOnline, postOrQueue } from "@/lib/offline";
import { X, Loader2, Volume2, VolumeX, WifiOff, UserCheck, UserX, Warehouse, ArrowRightCircle, ArrowUp, ArrowUpRight, ArrowUpLeft, CornerUpLeft, CornerUpRight, RotateCcw, Flag, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const OFF_ROUTE_M = 55;
const REROUTE_COOLDOWN = 25000;
const ARRIVE_M = 60;              // rayon de détection d'arrivée à l'arrêt
const OVERSPEED_TOLERANCE = 10;   // km/h au-dessus de la limite avant avertissement
const SPEED_WARN_COOLDOWN = 45000;
const PACK_KEY = (id) => `navpack_${id}`;

const fmtDist = (m) => (m == null ? "—" : m < 1000 ? `${Math.round(m / 10) * 10} m` : `${(m / 1000).toFixed(1)} km`);

function Maneuver({ type, modifier, className }) {
  const m = modifier || "";
  if (m === "uturn") return <RotateCcw className={className} />;
  if (m.includes("sharp left")) return <CornerUpLeft className={className} />;
  if (m.includes("sharp right")) return <CornerUpRight className={className} />;
  if (m.includes("left")) return <ArrowUpLeft className={className} />;
  if (m.includes("right")) return <ArrowUpRight className={className} />;
  if (type === "arrive") return <Flag className={className} />;
  return <ArrowUp className={className} />;
}

const R = 6371000;
const toRad = (d) => (d * Math.PI) / 180;
function haversine(a, b) {
  if (!a || !b || a.length < 2 || b.length < 2) return null;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]) * Math.cos(toRad((a[1] + b[1]) / 2));
  return Math.hypot(dLat, dLng) * R;
}
function distToLine(p, line) {
  // projection équirectangulaire locale (mètres) : suffisant pour < 100 km
  const lat0 = toRad(p[1]);
  const proj = (c) => [toRad(c[0]) * Math.cos(lat0) * R, toRad(c[1]) * R];
  const [px, py] = proj(p);
  let min = Infinity;
  for (let i = 1; i < line.length; i += 1) {
    const [ax, ay] = proj(line[i - 1]);
    const [bx, by] = proj(line[i]);
    const dx = bx - ax, dy = by - ay;
    const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy || 1e-9)));
    min = Math.min(min, Math.hypot(px - (ax + t * dx), py - (ay + t * dy)));
  }
  return min;
}

/** Navigation virage-par-virage Mapbox (fr-CA) : carte orientée conduite, voix, recalcul auto. */
export default function MapboxNav({ assignmentId, stopId, full = false, dest: initialDest = null, label, dropOff = false, center = null, onClose, onProgress, onNextTrip, onUseGoogleMaps }) {
  const box = useRef(null);
  const map = useRef(null);
  const me = useRef(null);
  const nav = useRef(null);
  const stepIdx = useRef(0);
  const spoken = useRef(new Set());
  const offCount = useRef(0);
  const lastReroute = useRef(0);
  const arrivedFor = useRef(null);
  const destMark = useRef(null);
  const fallbackTimer = useRef(null);
  const pendingGeom = useRef(null);
  const lastSpeedWarn = useRef(0);
  const posRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState(null);
  const [voice, setVoice] = useState(true);
  const [offline, setOffline] = useState(!isOnline());
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState(null);
  const [atStop, setAtStop] = useState(false);
  const [dest, setDest] = useState(initialDest);
  const [finished, setFinished] = useState(false);
  const [speed, setSpeed] = useState(null);
  const [limit, setLimit] = useState(null);
  const [view, setView] = useState(null);   // {primary, secondary, dist, type, modifier, eta, remain}
  const [noToken] = useState(!mapboxgl.accessToken);
  const [approx, setApprox] = useState(false);
  const [noGps, setNoGps] = useState(false);

  const speak = (text) => {
    if (!voice || !text || !("speechSynthesis" in window)) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "fr-CA";
    u.rate = 1;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  };

  const drawRoute = (geometry) => {
    if (!map.current || !geometry?.length) return;
    // le style n'est pas encore chargé : on garde le tracé et on le pose au « load »
    if (!map.current.isStyleLoaded()) {
      pendingGeom.current = geometry;
      return;
    }
    const data = { type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: geometry } };
    const src = map.current.getSource("nav-route");
    if (src) return src.setData(data);
    map.current.addSource("nav-route", { type: "geojson", data });
    map.current.addLayer({
      id: "nav-route-casing", type: "line", source: "nav-route",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": "#1558b0", "line-width": 13 },
    });
    map.current.addLayer({
      id: "nav-route", type: "line", source: "nav-route",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": "#1a73e8", "line-width": 8.5 },
    });
  };

  const drawDest = (t) => {
    if (!map.current || !t?.lat) return;
    if (!destMark.current) {
      const el = document.createElement("div");
      el.className = "nav-dest-pin";
      destMark.current = new mapboxgl.Marker({ element: el, anchor: "bottom" });
    }
    destMark.current.setLngLat([t.lng, t.lat]).addTo(map.current);
  };

  const fetchRoute = async (pos, silent = false, destOverride) => {
    const useDest = destOverride !== undefined ? destOverride : dest;
    if (!isOnline()) {
      const pack = localStorage.getItem(PACK_KEY(assignmentId));
      if (pack) {
        nav.current = JSON.parse(pack);
        stepIdx.current = 0;
        spoken.current.clear();
        drawRoute(nav.current.geometry);
        setTarget(nav.current.target || null);
        setOffline(true);
        return nav.current;
      }
      toast.error("Hors réseau et aucun itinéraire enregistré");
      return null;
    }
    try {
      const { data } = await api.post(`/driver/routes/${assignmentId}/mapbox-nav`, {
        lat: pos ? pos[1] : undefined, lng: pos ? pos[0] : undefined,
        stop_id: stopId || undefined, full, dest: useDest || undefined,
      });
      setApprox(!!data.origin_approx);
      nav.current = data;
      stepIdx.current = 0;
      spoken.current.clear();
      arrivedFor.current = null;
      drawRoute(data.geometry);
      drawDest(data.target);
      setTarget(data.target || null);
      setAtStop(false);
      if (!data.target && !useDest) setFinished(true);
      localStorage.setItem(PACK_KEY(assignmentId), JSON.stringify(data));
      setOffline(false);
      if (data.steps?.length) {
        const s0 = data.steps[0];
        setView({
          primary: s0.banner?.primary || s0.instruction,
          secondary: s0.banner?.secondary || data.steps[1]?.banner?.primary || "",
          dist: haversine(pos, s0.location), type: s0.type, modifier: s0.modifier,
          remain: data.steps.reduce((a, x) => a + x.distance, 0),
          mins: data.duration_min, name: s0.name,
          arrival: new Date(Date.now() + data.duration_min * 60000).toTimeString().slice(0, 5),
        });
      }
      if (data.geometry?.length && !pos) {
        const b = data.geometry.reduce((acc, c) => [
          Math.min(acc[0], c[0]), Math.min(acc[1], c[1]),
          Math.max(acc[2], c[0]), Math.max(acc[3], c[1])],
          [180, 90, -180, -90]);
        map.current?.fitBounds([[b[0], b[1]], [b[2], b[3]]],
          { padding: { top: 200, bottom: 220, left: 40, right: 40 }, pitch: 0, duration: 900 });
      }
      if (silent) speak("Itinéraire recalculé");
      return data;
    } catch (e) {
      const msg = apiError(e.response?.data?.detail) || "Navigation indisponible";
      setFatal(msg);                 // panneau clair + repli Google Maps
      toast.error(msg);
      return null;
    } finally { setLoading(false); }
  };

  /** Enregistre l'arrêt et repart automatiquement vers le suivant. */
  const markStop = async (status) => {
    if (!target || busy) return;
    setBusy(true);
    try {
      await postOrQueue(`/driver/routes/${assignmentId}/stop/${target.id}/status`, { status }, { critical: true });
      onProgress?.();
      const next = await fetchRoute(posRef.current || [0, 0]);
      if (next?.target) {
        speak(`Prochain arrêt : ${next.target.address}`);
        toast.success(`Arrêt enregistré — direction ${next.target.address}`);
      } else {
        setFinished(true);
        speak("Dernier arrêt fait. Retour au garage ou prochain trajet ?");
        toast.success("Tous les arrêts sont faits");
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Impossible d'enregistrer l'arrêt");
    } finally { setBusy(false); }
  };

  /** Dernier arrêt fait: naviguer vers le garage (ou l'école) pour finir le trajet. */
  const goFinal = async (which) => {
    setBusy(true);
    setFinished(false);
    setDest(which);
    setLoading(true);
    const pos = posRef.current || await new Promise((res) => {
      if (!("geolocation" in navigator)) return res(null);
      navigator.geolocation.getCurrentPosition(
        (p) => res([p.coords.longitude, p.coords.latitude]), () => res(null),
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 });
    });
    if (!pos) {
      toast.error("Position GPS indisponible");
      setLoading(false); setFinished(true); setBusy(false);
      return;
    }
    posRef.current = pos;
    const r = await fetchRoute(pos, false, which);
    if (r) speak(which === "garage" ? "Direction le garage" : "Direction l'école");
    else setFinished(true);
    setBusy(false);
  };

  useEffect(() => {
    const token = mapboxgl.accessToken;
    if (!token) {
      setFatal("Aucun jeton Mapbox n'est configuré pour la carte. Utilisez Google Maps en attendant.");
      setLoading(false);
      return undefined;
    }
    if (!token.startsWith("pk.")) {
      setFatal("Le jeton de carte enregistré est un jeton secret : les navigateurs le refusent. "
        + "Un administrateur doit enregistrer le jeton Mapbox public.");
      setLoading(false);
      return undefined;
    }
    const canvas = document.createElement("canvas");
    const webgl = canvas.getContext("webgl2") || canvas.getContext("webgl");
    if (!webgl) {
      setFatal("Cet appareil n'affiche pas la carte vectorielle (WebGL désactivé). "
        + "Utilisez Google Maps pour la navigation.");
      setLoading(false);
      return undefined;
    }
    try {
      map.current = new mapboxgl.Map({
        container: box.current,
        style: "mapbox://styles/mapbox/navigation-day-v1",
        center: center || [-72.98, 45.12], zoom: 17.5, pitch: 55, attributionControl: false,
      });
      map.current.on("load", () => {
        if (pendingGeom.current) {
          const g = pendingGeom.current;
          pendingGeom.current = null;
          drawRoute(g);
        }
        if (nav.current?.target) drawDest(nav.current.target);
      });
      map.current.on("error", (e) => {
        const msg = e?.error?.message || "";
        if (/401|403|token/i.test(msg)) {
          setFatal("La carte est refusée par Mapbox (jeton invalide). Utilisez Google Maps.");
          setLoading(false);
        }
      });
    } catch (e) {
      setFatal("La carte n'a pas pu être chargée. Utilisez Google Maps.");
      setLoading(false);
      return undefined;
    }
    return () => { window.speechSynthesis?.cancel(); map.current?.remove(); map.current = null; };
  }, []); // eslint-disable-line

  useEffect(() => {
    let watch = null;
    const onPos = async (p) => {
      const cur = [p.coords.longitude, p.coords.latitude];
      const heading = p.coords.heading;
      posRef.current = cur;
      if (fallbackTimer.current) { clearTimeout(fallbackTimer.current); fallbackTimer.current = null; }
      setNoGps(false);
      const kmh = Math.round(((p.coords.speed || 0) * 3.6) * 10) / 10;
      setSpeed(kmh);
      if (!map.current) return;
      if (!nav.current) { await fetchRoute(cur); map.current.jumpTo({ center: cur, zoom: 17 }); }
      if (!me.current) {
        const el = document.createElement("div");
        el.className = "nav-puck";
        el.innerHTML = '<span class="nav-puck-arrow"></span>';
        me.current = new mapboxgl.Marker({ element: el, pitchAlignment: "map" }).setLngLat(cur).addTo(map.current);
      } else me.current.setLngLat(cur);
      map.current.easeTo({
        center: cur, zoom: 17.5, pitch: 55,
        bearing: Number.isFinite(heading) ? heading : map.current.getBearing(),
        duration: 800, padding: { top: 260, bottom: 40, left: 0, right: 0 },
      });

      const n = nav.current;
      if (!n?.steps?.length) return;

      // avance dans les manœuvres
      let i = stepIdx.current;
      while (i < n.steps.length - 1 && haversine(cur, n.steps[i].location) < 35) i += 1;
      stepIdx.current = i;
      const step = n.steps[i];
      const d = haversine(cur, step.location);

      // annonces vocales une seule fois, au bon moment
      (step.voice || []).forEach((v, k) => {
        const key = `${i}-${k}`;
        if (!spoken.current.has(key) && d != null && d <= v.d + 25) {
          spoken.current.add(key);
          speak(v.text);
        }
      });

      const remain = n.steps.slice(i).reduce((s, x) => s + x.distance, 0);
      const mins = Math.round(n.steps.slice(i).reduce((s, x) => s + x.duration, 0) / 60);
      setView({
        primary: step.banner?.primary || step.instruction,
        secondary: step.banner?.secondary || n.steps[i + 1]?.banner?.primary || "",
        dist: d, type: step.type, modifier: step.modifier,
        remain, mins, name: step.name,
        arrival: new Date(Date.now() + mins * 60000).toTimeString().slice(0, 5),
      });

      // sortie de route → recalcul automatique
      if (n.geometry?.length > 1) {
        const off = distToLine(cur, n.geometry);
        if (off > OFF_ROUTE_M) offCount.current += 1; else offCount.current = 0;
        if (offCount.current >= 2 && Date.now() - lastReroute.current > REROUTE_COOLDOWN && isOnline()) {
          offCount.current = 0;
          lastReroute.current = Date.now();
          await fetchRoute(cur, true);
          return;
        }
      }

      // limite de vitesse du tronçon en cours (données Mapbox, repli sur la fiche d'arrêt)
      if (n.geometry?.length) {
        let best = 0, bestD = Infinity;
        for (let k = 0; k < n.geometry.length; k += 1) {
          const d = haversine(cur, n.geometry[k]);
          if (d != null && d < bestD) { bestD = d; best = k; }
        }
        const lim = (n.maxspeed || [])[Math.min(best, (n.maxspeed || []).length - 1)] || n.target?.speed_limit || null;
        setLimit(lim);
        if (lim && kmh > lim + OVERSPEED_TOLERANCE) {
          if (Date.now() - lastSpeedWarn.current > SPEED_WARN_COOLDOWN) {
            lastSpeedWarn.current = Date.now();
            speak("Vous dépassez la limite de vitesse");
            api.post("/driver/speeding", {
              assignment_id: assignmentId, lat: cur[1], lng: cur[0],
              speed_kmh: kmh, limit_kmh: lim,
            }).catch(() => {});
          }
        }
      }

      // arrivée à l'arrêt → statut ARRIVÉ automatique et boutons d'action
      const tgt = nav.current?.target;
      if (tgt?.lat) {
        const dStop = haversine(cur, [tgt.lng, tgt.lat]);
        if (dStop != null && dStop < ARRIVE_M) {
          setAtStop(true);
          if (arrivedFor.current !== tgt.id) {
            arrivedFor.current = tgt.id;
            speak("Vous êtes arrivé à l'arrêt");
            postOrQueue(`/driver/routes/${assignmentId}/stop/${tgt.id}/status`, { status: "ARRIVED" },
              { critical: false }).then(() => onProgress?.()).catch(() => {});
          }
        } else if (dStop != null && dStop > ARRIVE_M * 2) setAtStop(false);
      }
    };
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(onPos, () => { setNoGps(true); fetchRoute(null); },
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 12000 });
      watch = navigator.geolocation.watchPosition(onPos, () => {}, { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 });
      // si aucune position au bout de 8 s: itinéraire calculé depuis la dernière position connue
      fallbackTimer.current = setTimeout(() => {
        if (!posRef.current && !nav.current) { setNoGps(true); fetchRoute(null); }
      }, 8000);
    } else { setNoGps(true); fetchRoute(null); }
    return () => {
      if (watch != null) navigator.geolocation.clearWatch(watch);
      if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
    };
  }, []); // eslint-disable-line

  const over = limit != null && speed != null && speed > limit + OVERSPEED_TOLERANCE;

  return (
    <div className="fixed inset-0 z-[9998] bg-slate-200" data-testid="mapbox-nav">
      <div ref={box} className="absolute inset-0" />

      {fatal && (
        <div className="absolute inset-0 z-20 bg-slate-950/95 flex flex-col items-center justify-center p-6 text-center"
          data-testid="nav-fatal">
          <AlertTriangle className="w-12 h-12 text-amber-400 mb-4" />
          <div className="text-white font-display text-xl font-extrabold mb-2">Guidage intégré indisponible</div>
          <p className="text-sm text-white/70 max-w-sm mb-7">{fatal}</p>
          <button onClick={() => { setFatal(null); onClose(); onUseGoogleMaps?.(); }}
            data-testid="nav-fallback-gmaps"
            className="w-full max-w-xs h-16 rounded-2xl bg-blue-600 text-white font-display text-lg font-extrabold">
            OUVRIR DANS GOOGLE MAPS
          </button>
          <button onClick={onClose} data-testid="nav-fatal-close"
            className="mt-3 text-sm text-white/60 underline">Revenir aux arrêts</button>
        </div>
      )}

      <div className="absolute top-0 left-0 right-0 p-2.5 pt-3">
        <div className="rounded-2xl bg-[#137333] text-white px-3.5 py-3 flex items-center gap-3 shadow-2xl">
          <Maneuver type={view?.type} modifier={view?.modifier} className="w-12 h-12 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-display text-3xl font-extrabold leading-none" data-testid="nav-distance">
              {loading ? "…" : fmtDist(view?.dist)}
            </div>
            <div className="text-sm font-semibold leading-snug mt-1 line-clamp-2" data-testid="nav-instruction">
              {loading ? "Calcul de l'itinéraire…" : view?.primary || "En route"}
            </div>
          </div>
          {loading && <Loader2 className="w-5 h-5 animate-spin opacity-70" />}
        </div>
        {view?.secondary && (
          <div className="mt-1.5 inline-flex rounded-xl bg-[#0d5222] text-white/90 px-3 py-1.5 text-xs font-semibold max-w-full truncate" data-testid="nav-next">
            Puis · {view.secondary}
          </div>
        )}
        {(noToken || noGps || approx) && (
          <div className="mt-1.5 rounded-xl bg-amber-500 text-slate-900 px-3 py-1.5 text-xs font-bold" data-testid="nav-warning">
            {noToken ? "Carte indisponible : jeton Mapbox absent de cette version — redéployez l'application."
              : "GPS non disponible — itinéraire calculé depuis la dernière position connue. Activez la localisation."}
          </div>
        )}
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-2.5 pb-4 space-y-2">
        {finished && (
          <div className="rounded-2xl bg-slate-900/95 border border-emerald-500/40 p-3 space-y-2" data-testid="nav-finish-choice">
            <div className="text-center text-sm font-semibold text-emerald-400">Dernier arrêt fait — que faites-vous ?</div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => goFinal("garage")} disabled={busy} data-testid="nav-to-garage"
                className="h-[72px] rounded-2xl bg-blue-600 text-white font-display text-base font-extrabold flex flex-col items-center justify-center gap-1 active:scale-95 transition-transform disabled:opacity-50">
                <Warehouse className="w-6 h-6" /> RETOUR AU GARAGE
              </button>
              <button onClick={() => { window.speechSynthesis?.cancel(); onNextTrip?.(); }} disabled={busy} data-testid="nav-next-trip"
                className="h-[72px] rounded-2xl bg-emerald-500 text-slate-900 font-display text-base font-extrabold flex flex-col items-center justify-center gap-1 active:scale-95 transition-transform disabled:opacity-50">
                <ArrowRightCircle className="w-6 h-6" /> PROCHAIN TRAJET
              </button>
            </div>
          </div>
        )}
        {atStop && target && !finished && (
          <div className="grid grid-cols-2 gap-2" data-testid="nav-stop-actions">
            <button onClick={() => markStop("PICKED_UP")} disabled={busy} data-testid="nav-boarded"
              className="h-20 rounded-2xl bg-emerald-500 text-slate-900 font-display text-xl font-extrabold flex flex-col items-center justify-center gap-0.5 active:scale-95 transition-transform disabled:opacity-50">
              <UserCheck className="w-7 h-7" /> {dropOff ? "DÉBARQUÉ" : "EMBARQUÉ"}
            </button>
            <button onClick={() => markStop("ABSENT")} disabled={busy} data-testid="nav-absent"
              className="h-20 rounded-2xl bg-amber-500 text-slate-900 font-display text-xl font-extrabold flex flex-col items-center justify-center gap-0.5 active:scale-95 transition-transform disabled:opacity-50">
              <UserX className="w-7 h-7" /> ABSENT
            </button>
          </div>
        )}
        <div className="flex items-end justify-between gap-2 pb-1">
          <div className={`w-16 h-16 rounded-full flex flex-col items-center justify-center shadow-xl border-2 ${
            over ? "bg-red-600 border-red-300 animate-pulse" : "bg-white border-slate-200"}`} data-testid="nav-speed">
            <span className={`font-display text-2xl font-extrabold leading-none ${over ? "text-white" : "text-slate-900"}`}>
              {speed == null ? "—" : Math.round(speed)}
            </span>
            <span className={`text-[9px] font-bold ${over ? "text-white/80" : "text-slate-500"}`}>km/h</span>
          </div>
          {limit && (
            <div className="w-14 h-14 rounded-full bg-white border-[5px] border-red-600 flex items-center justify-center shadow-xl" data-testid="nav-limit">
              <span className="font-display text-xl font-extrabold text-slate-900 leading-none">{limit}</span>
            </div>
          )}
          <div className="flex-1" />
        </div>
        <div className="rounded-2xl bg-white px-4 py-3 flex items-center gap-3 text-slate-900 shadow-2xl">
          <div className="flex-1 min-w-0">
            <div className="font-display text-2xl font-extrabold leading-none text-blue-700" data-testid="nav-eta">
              {view ? `${view.mins} min` : "—"}
            </div>
            <div className="text-sm text-slate-600 mt-0.5">
              {fmtDist(view?.remain)}{view?.arrival ? ` · arrivée ${view.arrival}` : ""}
            </div>
            <div className="text-xs text-slate-500 truncate mt-0.5">{target?.address || label || "Prochain arrêt"}</div>
            {over && <div className="text-[11px] font-bold text-red-600 mt-0.5">Vous dépassez la limite</div>}
            {offline && <span className="flex items-center gap-1 text-amber-600 text-[11px] font-semibold mt-0.5"><WifiOff className="w-3 h-3" /> hors réseau</span>}
          </div>
          <button onClick={() => { setVoice((v) => !v); window.speechSynthesis?.cancel(); }}
            data-testid="nav-voice" className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
            {voice ? <Volume2 className="w-6 h-6 text-blue-700" /> : <VolumeX className="w-6 h-6 text-slate-400" />}
          </button>
          <button onClick={() => { window.speechSynthesis?.cancel(); onClose(); }}
            data-testid="nav-close" className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
            <X className="w-6 h-6 text-slate-700" />
          </button>
        </div>
      </div>
    </div>
  );
}
